import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from './api/axiosInstance';
import base_url from './api/Config';
import TopBar from './component/TopBar';
import { Circles } from 'react-loader-spinner';

const InfoRow = ({ label, value, last }) => value ? (
  <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom: last ? 'none' : '1px solid #f0f0f0', fontSize:14 }}>
    <span style={{ color:'#888' }}>{label}</span>
    <span style={{ fontWeight:600, color:'#1d2e77', maxWidth:'60%', textAlign:'right' }}>{value}</span>
  </div>
) : null;

function CreditCheck() {
  const [loading, setLoading]   = useState(true);
  const [result,  setResult]    = useState(null);
  const [error,   setError]     = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const params        = new URLSearchParams(location.search);
  const applicationId = params.get('applicationId');
  const customerId    = params.get('customerId');

  useEffect(() => {
    const fetchScore = async () => {
      try {
        const response = await api.post(`${base_url}/api/experian/softCheck?customerId=${customerId}`);

        // full response log — browser console mein dekho kya fields aa rahe hain
        console.log('Experian full response:', JSON.stringify(response?.data, null, 2));

        const d         = response?.data?.experianResponse?.data || {};
        const applicant = d.applicant || {};
        const summary   = d.accountSummary || {};
        const score     = parseInt(d.bureauScore || 0);

        const details = {
          score,
          name:       [applicant.firstName, applicant.lastName].filter(Boolean).join(' ') || null,
          pan:        applicant.pan     || null,
          dob:        applicant.dob     || null,
          gender:     applicant.gender  || null,
          mobile:     applicant.mobile  || null,
          state:      applicant.state   || null,
          pinCode:    applicant.pinCode || null,
          reportDate: d.reportDate      || null,
          reportNo:   d.reportNumber    || null,
          totalAcc:   summary.total     || null,
          activeAcc:  summary.active    || null,
          closedAcc:  summary.closed    || null,
          balance:    summary.totalBalance || null,
          eligible: score >= 650 || (score >= 0 && score <= 300),
        };
        setResult(details);
      } catch (err) {
        setError(err?.response?.data?.message || 'Credit score fetch failed. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchScore();
  }, [customerId, retryCount]);

  const scoreColor = (s) => {
    if (!s) return '#888';
    if (s <= 300) return '#1976d2';
    if (s < 650)  return '#e53935';
    return '#2e7d32';
  };

  const scoreLabel = (s) => {
    if (!s || s <= 300) return 'New to Credit';
    if (s < 650)        return 'Poor';
    if (s < 750)        return 'Fair';
    return 'Good';
  };

  return (
    <div className="app-page">
      <TopBar title="Credit Check" backPath={`/Address?applicationId=${applicationId}&customerId=${customerId}`} />

      <div className="app-content">

        {/* ── LOADING ── */}
        {loading && (
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', paddingTop:60, gap:16 }}>
            <Circles height={60} width={60} color="#1d2e77" />
            <p style={{ color:'#555', fontSize:14, fontWeight:600 }}>Fetching credit report...</p>
          </div>
        )}

        {/* ── ERROR ── */}
        {!loading && error && (
          <div className="app-card" style={{ textAlign:'center' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>⚠️</div>
            <p style={{ color:'#e53935', fontWeight:700, fontSize:16, marginBottom:8 }}>Something went wrong</p>
            <p style={{ color:'#666', fontSize:13, marginBottom:20 }}>{error}</p>
            <button className="btn-primary-app" onClick={() => { setError(null); setLoading(true); setRetryCount(c => c + 1); }}>
              Retry
            </button>
          </div>
        )}

        {/* ── RESULT ── */}
        {!loading && result && (
          <>
            {/* Score card */}
            <div style={{
              background: 'linear-gradient(135deg,#1d2e77 0%,#2a3f99 100%)',
              borderRadius: 14, padding: '14px 16px', marginBottom: 12,
              display: 'flex', alignItems: 'center', gap: 14,
              boxShadow: '0 4px 18px rgba(29,46,119,0.28)',
            }}>
              <div style={{
                width: 72, height: 72, borderRadius: '50%', flexShrink: 0,
                background: scoreColor(result.score),
                display: 'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                boxShadow: '0 0 0 4px rgba(255,255,255,0.15)',
              }}>
                <span style={{ color:'#fff', fontWeight:800, fontSize:22, lineHeight:1 }}>{result.score || '—'}</span>
                <span style={{ color:'rgba(255,255,255,0.8)', fontSize:9 }}>{scoreLabel(result.score)}</span>
              </div>
              <div>
                <p style={{ color:'rgba(255,255,255,0.65)', fontSize:11, margin:'0 0 4px' }}>Bureau Score</p>
                {result.eligible ? (
                  <div style={{ background:'rgba(46,125,50,0.3)', borderRadius:8, padding:'4px 12px', display:'inline-block' }}>
                    <span style={{ color:'#a5d6a7', fontWeight:700, fontSize:13 }}>✓ Eligible for Loan</span>
                  </div>
                ) : (
                  <div style={{ background:'rgba(229,57,53,0.3)', borderRadius:8, padding:'4px 12px', display:'inline-block' }}>
                    <span style={{ color:'#ef9a9a', fontWeight:700, fontSize:13 }}>✗ Not Eligible</span>
                  </div>
                )}
              </div>
            </div>

            {/* Details card */}
            <div className="app-card">
              <p style={{ fontWeight:700, color:'#1d2e77', fontSize:14, marginBottom:12, paddingBottom:6, borderBottom:'2px solid #e8eaf6' }}>
                Personal Details
              </p>
              <InfoRow label="Name"          value={result.name}       />
              <InfoRow label="PAN"           value={result.pan}        />
              <InfoRow label="Date of Birth" value={result.dob}        />
              <InfoRow label="Gender"        value={result.gender}     />
              <InfoRow label="Mobile"        value={result.mobile}     />
              <InfoRow label="Pin Code"      value={result.pinCode}    />

              {(result.totalAcc || result.activeAcc || result.closedAcc || result.balance) && (
                <>
                  <p style={{ fontWeight:700, color:'#1d2e77', fontSize:13, margin:'14px 0 8px', paddingTop:10, borderTop:'2px solid #e8eaf6' }}>
                    Account Summary
                  </p>
                  <div style={{ display:'flex', gap:8, marginBottom:4 }}>
                    {[
                      { label:'Total',   val: result.totalAcc,  color:'#1d2e77' },
                      { label:'Active',  val: result.activeAcc, color:'#2e7d32' },
                      { label:'Closed',  val: result.closedAcc, color:'#888'    },
                    ].map(s => (
                      <div key={s.label} style={{ flex:1, background:'#f8f9ff', borderRadius:8, padding:'8px 6px', textAlign:'center' }}>
                        <p style={{ fontSize:10, color:'#aaa', margin:'0 0 2px' }}>{s.label}</p>
                        <p style={{ fontWeight:700, fontSize:18, color:s.color, margin:0 }}>{s.val}</p>
                      </div>
                    ))}
                  </div>
                  <InfoRow label="Total Balance" value={result.balance ? `₹${result.balance}` : null} last />
                </>
              )}
            </div>

            {/* Action */}
            {result.eligible ? (
              <button
                className="btn-primary-app"
                onClick={() => navigate(`/BankDetail?applicationId=${applicationId}&customerId=${customerId}`)}
              >
                Next — Bank Details
              </button>
            ) : (
              <div style={{ background:'#ffebee', borderRadius:12, padding:16, textAlign:'center', marginBottom:16 }}>
                <p style={{ color:'#c62828', fontWeight:700, fontSize:15, margin:'0 0 6px' }}>Loan Application Rejected</p>
                <p style={{ color:'#e57373', fontSize:13, margin:0 }}>
                  Score below 650 — not eligible to proceed.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default CreditCheck;
